import React, { useState } from 'react';
import SmsModal from './SmsModal';
import { sendSms, formatPhoneNumbers } from '../controller/sms-controller';
import { toast } from 'react-toastify';

const ContactsTable = ({ data: initialData }) => {
  // Changed to use initialData for the prop and data for state
  const [data, setData] = useState(initialData);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  
  // Update data if initialData changes
  React.useEffect(() => {
    setData(initialData);
  }, [initialData]);
  
  // Filter contacts based on search term
  const filteredContacts = data.filter(contact => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      contact.firstName.toLowerCase().includes(searchTermLower) ||
      contact.lastName.toLowerCase().includes(searchTermLower) ||
      contact.phone.toLowerCase().includes(searchTermLower)
    );
  });
  
  // Sort contacts based on sort config
  const sortedContacts = React.useMemo(() => {
    if (!sortConfig.key) return filteredContacts;
    
    return [...filteredContacts].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredContacts, sortConfig]);
  
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortConfig.direction === 'ascending' ? (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#174540]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#174540]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };
  
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedContacts(sortedContacts.map((_, i) => i));
    } else {
      setSelectedContacts([]);
    }
  };
  
  const handleSelectContact = (index) => {
    if (selectedContacts.includes(index)) {
      setSelectedContacts(selectedContacts.filter(i => i !== index));
    } else {
      setSelectedContacts([...selectedContacts, index]);
    }
  };
  
  // Function to delete a single contact
  const handleDeleteContact = (index) => {
    // Get the contact from sorted list
    const contactToDelete = sortedContacts[index];
    
    // Find the contact in the original data
    const newData = data.filter(contact => 
      !(contact.firstName === contactToDelete.firstName && 
        contact.lastName === contactToDelete.lastName && 
        contact.phone === contactToDelete.phone)
    );
    
    setData(newData);
    
    // If the contact was selected, remove from selection
    if (selectedContacts.includes(index)) {
      setSelectedContacts(selectedContacts.filter(i => i !== index));
    }
    
    toast.success('Contact supprimé avec succès');
  };
  
  // Function to delete multiple selected contacts
  const handleDeleteSelected = () => {
    if (selectedContacts.length === 0) return;
    
    // Get contacts to delete from sorted list
    const contactsToDelete = selectedContacts.map(index => sortedContacts[index]);
    
    // Remove these contacts from the data
    const newData = data.filter(contact => 
      !contactsToDelete.some(toDelete => 
        toDelete.firstName === contact.firstName && 
        toDelete.lastName === contact.lastName && 
        toDelete.phone === contact.phone
      )
    );
    
    setData(newData);
    setSelectedContacts([]);
    
    toast.success(`${contactsToDelete.length} contact(s) supprimé(s) avec succès`);
  };
  
  // Updated function to send SMS using the controller
  const handleSendSms = async (message) => {
    if (selectedContacts.length === 0 || !message.trim()) {
      return;
    }

    // Collect phone numbers from selected contacts
    const rawPhoneNumbers = selectedContacts.map(index => sortedContacts[index].phone);
    
    // Format phone numbers using the controller utility
    const phoneNumbers = formatPhoneNumbers(rawPhoneNumbers);

    setIsSending(true);
    setSmsResult(null);

    try {
      // Call the controller function instead of making the fetch directly
      const result = await sendSms(phoneNumbers, message);
      
      if (result.success) {
        setSmsResult({
          success: true,
          message: result.message
        });
        // Close modal on success after a short delay
        setTimeout(() => {
          setIsSmsModalOpen(false);
        }, 1500);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      setSmsResult({
        success: false,
        message: error.message || 'Erreur lors de l\'envoi des SMS.'
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
          <h3 className="text-lg font-medium text-gray-900 mb-4 sm:mb-0">Contacts importés</h3>
          <div className="relative flex-grow max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              className="focus:ring-[#174540] focus:border-[#174540] block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              placeholder="Rechercher un contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>
      
      <div className="flex justify-between items-center px-6 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center">
          <input
            type="checkbox"
            className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
            checked={selectedContacts.length === sortedContacts.length && sortedContacts.length > 0}
            onChange={handleSelectAll}
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            {selectedContacts.length} sélectionné(s)
          </span>
        </div>
        
        {selectedContacts.length > 0 && (
          <div className="flex space-x-3">
            {/* SMS Button */}
            <button
              type="button"
              onClick={() => setIsSmsModalOpen(true)}
              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              Envoyer SMS
            </button>
        
            
            {/* Delete Button */}
            <button
              type="button"
              className="inline-flex items-center px-2.5 py-1.5 border border-red-700 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={handleDeleteSelected}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Supprimer
            </button>
          </div>
        )}
      </div>
      
      <div className="max-h-[480px] overflow-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                <span className="sr-only">Sélectionner</span>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort('firstName')}
              >
                <div className="flex items-center">
                  <span>Prénom</span>
                  <span className="ml-2">{getSortIcon('firstName')}</span>
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort('lastName')}
              >
                <div className="flex items-center">
                  <span>Nom</span>
                  <span className="ml-2">{getSortIcon('lastName')}</span>
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort('phone')}
              >
                <div className="flex items-center">
                  <span>Téléphone</span>
                  <span className="ml-2">{getSortIcon('phone')}</span>
                </div>
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedContacts.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p className="mt-4 text-gray-500 text-base">Aucun contact trouvé</p>
                  {searchTerm && (
                    <button 
                      className="mt-2 text-sm text-[#174540] hover:text-[#0d2926] font-medium"
                      onClick={() => setSearchTerm('')}
                    >
                      Effacer la recherche
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              sortedContacts.map((contact, index) => (
                <tr 
                  key={index} 
                  className={selectedContacts.includes(index) ? 'bg-blue-50' : 'hover:bg-gray-50'}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
                      checked={selectedContacts.includes(index)}
                      onChange={() => handleSelectContact(index)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0 bg-[#174540] bg-opacity-20 rounded-full flex items-center justify-center text-[#174540]">
                        {contact.firstName.charAt(0)}{contact.lastName.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{contact.firstName}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {contact.lastName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {contact.phone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {/* Removed edit icon and kept only delete button */}
                    <button 
                      className="text-red-600 hover:text-red-900"
                      title="Supprimer"
                      onClick={() => handleDeleteContact(index)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Affichage de <span className="font-medium">{sortedContacts.length}</span> sur <span className="font-medium">{data.length}</span> contacts
        </div>
      </div>
      
      {/* SMS Modal with updated props */}
      <SmsModal
        isOpen={isSmsModalOpen}
        onClose={() => {
          setIsSmsModalOpen(false);
          // Clear any previous results when closing
          setSmsResult(null);
        }}
        onSend={handleSendSms}
        isSending={isSending}
        contactCount={selectedContacts.length}
        result={smsResult} // Pass the result to show in the modal
      />
    </div>
  );
};

export default ContactsTable;